import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faClock, faChefHat, faAward, faHeart, faCalendar, faPhone, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { restaurantInfo, menuItems, testimonials } from '../data/mock';
import { getVideoUrl } from '../config/cdn';

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);
  const [currentCarouselImage, setCurrentCarouselImage] = useState(0);
  const [openFAQ, setOpenFAQ] = useState(null);
  
  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };
  
  // Force scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const imageTimer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % 5);
    }, 4000);
    return () => clearInterval(imageTimer);
  }, []);

  const featuredDishes = menuItems.filter(item => item.popular).slice(0, 3);

  const restaurantImages = [
    {
      src: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=800&fit=crop&auto=format",
      alt: "Interior elegante del restaurante"
    },
    {
      src: "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=600&h=800&fit=crop&auto=format",
      alt: "Plato gourmet especialidad"
    },
    {
      src: "https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=600&h=800&fit=crop&auto=format",
      alt: "Evento privado en el restaurante"
    },
    {
      src: "https://images.unsplash.com/photo-1551218372-6b41ad6c3d9b?w=600&h=800&fit=crop&auto=format",
      alt: "Creación culinaria del chef"
    },
    {
      src: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&h=800&fit=crop&auto=format",
      alt: "Mesa con platos gourmet"
    }
  ];

  return (
    <div className="page-transition">
      {/* Hero Section - Noma Style */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={getVideoUrl("Hero del home.mp4")} type="video/mp4" />
          </video>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Bottom Left Elements */}
        <div className="absolute bottom-8 left-4 right-4 md:left-6 md:right-auto text-white z-20 max-w-2xl">
          {/* Hero Title */}
          <div className="mb-4 md:mb-6">
            <h1 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-wide">
              Sabores de siempre, experiencia de hoy.
            </h1>
            <p className="font-light text-base sm:text-lg md:text-xl text-white/90 mt-2 md:mt-4 leading-relaxed italic">
              Un espacio íntimo donde la tradición se sirve con modernidad.
            </p>
          </div>

          {/* Season Badge */}
          <div className="mb-4 md:mb-6">
            <div className="bg-red-600 text-white px-4 py-2 md:px-6 md:py-2 rounded-sm font-light tracking-wide inline-block text-sm md:text-base">
              Menú Temporada 2025-2026
            </div>
          </div>

          {/* CTA Button */}
          <div className="mb-6 md:mb-8">
            <Link 
              to="/reservas" 
              className="border border-white text-white px-8 py-3 font-bold tracking-wide hover:bg-white hover:text-black transition-all duration-300 inline-flex items-center justify-center w-auto"
            >
              <span>RESERVAR MESA</span>
            </Link>
          </div>
        </div>

        {/* Bottom Right Location Info - Hidden on mobile, visible on md+ */}
        <div className="hidden md:block absolute bottom-8 right-6 text-white z-20">
          <div className="flex items-center space-x-4 text-sm font-light">
            <span>La Habana, Cuba</span>
            <span className="text-xs">•</span>
            <span>32°C</span>
          </div>
          <div className="flex items-center space-x-4 text-sm font-light mt-1">
            <span>En nuestros espacios</span>
            <span className="text-xs">•</span>
            <span>18°C</span>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-white">
        <div className="container-custom max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="font-sans text-3xl md:text-6xl font-light text-gray-900 leading-tight mb-4 md:mb-6">
              Reconocidos por <span className="text-green-600 font-normal italic">la excelencia</span>
            </h2>
            <p className="font-sans text-base md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Nuestros comensales comparten su experiencia excepcional. Cada reseña refleja nuestro compromiso con la calidad y el servicio.
            </p>
          </div>

          {/* Reviews Cards - Centered Below Header */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            
            {/* TripAdvisor */}
            <a 
              href="https://www.tripadvisor.com/Restaurant_Review-g147271-d32870034-Reviews-Adastra_Restaurant_s-Havana_Ciudad_de_la_Habana_Province_Cuba.html#REVIEWS"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center text-center space-y-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer hover:scale-105"
            >
              <div>
                <img 
                  src="https://static.tacdn.com/img2/brand_refresh/Tripadvisor_lockup_horizontal_secondary_registered.svg" 
                  alt="TripAdvisor" 
                  className="h-8"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-center space-x-2 mb-2">
                  {[...Array(4)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                  <svg className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                    <defs>
                      <linearGradient id="half-star-trip" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="90%" stopColor="#fbbf24"/>
                        <stop offset="90%" stopColor="#d1d5db"/>
                      </linearGradient>
                    </defs>
                    <path fill="url(#half-star-trip)" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span className="text-lg font-bold text-gray-800 ml-2">4.9</span>
                </div>
                <p className="text-sm text-gray-600">Basado en 83 reseñas</p>
                <p className="text-xs text-blue-600 mt-2 font-medium">Ver todas las reseñas →</p>
              </div>
            </a>

            {/* Google Reviews */}
            <a 
              href="https://www.google.com/maps/search/ADASTRA+Restaurant+Habana+Vieja+Cuba"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center text-center space-y-4 p-6 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer hover:scale-105"
            >
              <div>
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-8 h-8" viewBox="0 0 24 24">
                    <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="text-lg font-semibold text-gray-700">Google</span>
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-center space-x-2 mb-2">
                  {[...Array(4)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                  <svg className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                    <defs>
                      <linearGradient id="half-star-google2" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="90%" stopColor="#fbbf24"/>
                        <stop offset="90%" stopColor="#d1d5db"/>
                      </linearGradient>
                    </defs>
                    <path fill="url(#half-star-google2)" d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span className="text-lg font-bold text-gray-800 ml-2">4.9</span>
                </div>
                <p className="text-sm text-gray-600">Basado en 108 reseñas</p>
                <p className="text-xs text-blue-600 mt-2 font-medium">Ver todas las reseñas →</p>
              </div>
            </a>

          </div>

        </div>
      </section>

      {/* Call to Action - Reserva tu Experiencia */}
      <section className="relative pt-8 pb-12 md:py-20 overflow-hidden bg-white">
        <div className="container-custom max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Content */}
            <div className="text-center lg:text-left">
              <h2 className="font-sans text-4xl md:text-5xl font-light text-gray-900 mb-6">
                Reserva tu <span className="font-normal italic text-green-600">Experiencia</span>
              </h2>

              <p className="font-sans text-lg text-gray-600 mb-8 max-w-2xl">
                Cada momento en ADASTRA es único. No dejes que se escape tu próxima experiencia inolvidable.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to="/reservas"
                  className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-full max-w-[400px] md:w-auto md:max-w-none"
                >
                  <span>RESERVAR MESA</span>
                </Link>
                
                <a 
                  href="https://wa.me/5363518498?text=Hola%20ADASTRA,%20me%20interesa%20hacer%20una%20reserva%20o%20consulta."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-full max-w-[400px] md:w-auto md:max-w-none"
                >
                  <span>CONTACTAR AHORA</span>
                </a>
              </div>
            </div>

            {/* Right Side - Image Carousel */}
            <div className="relative">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                {/* Carousel Images */}
                <div className="relative w-full h-full">
                  <img
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Pescado Boreal.JPG"
                    alt="Pescado Boreal"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${currentCarouselImage === 0 ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <img
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Corte de res 2.jpg"
                    alt="Corte de Res"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${currentCarouselImage === 1 ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <img
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Langosta Isleña 2.JPG"
                    alt="Langosta Isleña"
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${currentCarouselImage === 2 ? 'opacity-100' : 'opacity-0'}`}
                  />
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={() => setCurrentCarouselImage((prev) => (prev - 1 + 3) % 3)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full transition-all duration-200 shadow-lg"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentCarouselImage((prev) => (prev + 1) % 3)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-3 rounded-full transition-all duration-200 shadow-lg"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="w-5 h-5" />
                </button>

                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {[0, 1, 2].map((index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentCarouselImage(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        currentCarouselImage === index ? 'bg-white w-8' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Nuestra Historia - Nuevo Diseño */}
      <section className="bg-gray-50 py-14 md:py-24">
        <div className="container-custom max-w-7xl mx-auto px-4 md:px-6">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            
            {/* Lado Izquierdo - Texto */}
            <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="font-sans text-3xl md:text-6xl font-light text-gray-900 leading-tight mb-4 md:mb-6">
                  <span className="text-green-600 font-normal italic">Historia</span>
                </h2>
                <div className="w-24 h-0.5 bg-gradient-to-r from-green-600 to-transparent mb-6 md:mb-8"></div>
              </div>
              
              <div className="space-y-6">
                <p className="font-sans text-lg text-gray-600 leading-relaxed">
                  En el corazón de La Habana Vieja, ADASTRA nació en 2024 con una visión clara: rescatar 
                  los sabores tradicionales cubanos y presentarlos con un toque contemporáneo que honre 
                  nuestras raíces mientras abraza la innovación.
                </p>
                
                <p className="font-sans text-lg text-gray-600 leading-relaxed">
                  Desde nuestros primeros días, hemos sido más que un restaurante. Somos un puente entre 
                  la rica tradición culinaria de Cuba y las técnicas gastronómicas modernas. Cada plato 
                  cuenta una historia, cada ingrediente tiene su propósito, y cada experiencia se diseña 
                  para crear recuerdos inolvidables.
                </p>
                
                <p className="font-sans text-lg text-gray-600 leading-relaxed">
                  A lo largo de los años, hemos crecido de ser un sueño ambicioso a convertirnos en una 
                  referencia gastronómica en La Habana, reconocidos internacionalmente por nuestra 
                  propuesta única que celebra lo mejor de la cocina cubana con una perspectiva 
                  completamente nueva.
                </p>
              </div>
              
              {/* CTA */}
              <div className="pt-8">
                <Link 
                  to="/nosotros"
                  className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-auto"
                >
                  <span>VER MÁS</span>
                </Link>
              </div>
            </div>
            
            {/* Lado Derecho - Collage Orgánico de 6 Imágenes Reales de ADASTRA */}
            <div className="relative">
              
              {/* Fila Superior - Fachada (horizontal panorámica) */}
              <div className="mb-4">
                <div className="relative overflow-hidden rounded-2xl shadow-lg h-[320px]">
                  <img 
                    src="/assets/images/restaurant/Fachada.JPG"
                    alt="Fachada del restaurante ADASTRA con hostess"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              
              {/* Fila Media - Layout asimétrico */}
              <div className="grid grid-cols-3 gap-4 mb-4 h-[280px]">
                
                {/* Entrada con jarron (vertical) */}
                <div className="relative overflow-hidden rounded-2xl shadow-lg h-full">
                  <img 
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Entrada%20con%20jarron%20y%20colecion.JPG"
                    alt="Entrada elegante de ADASTRA con búcaro verde y vista del bar"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Lamparas de patio (horizontal doble) */}
                <div className="col-span-2 relative overflow-hidden rounded-2xl shadow-lg h-full">
                  <img 
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Lamparas%20de%20patio%20y%20barra.JPG"
                    alt="Terraza tropical de ADASTRA con mesas de madera y plantas"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
              </div>
              
              {/* Fila Inferior - Tres imágenes equilibradas */}
              <div className="grid grid-cols-3 gap-4 h-[200px]">
                
                {/* Mesa grande */}
                <div className="relative overflow-hidden rounded-2xl shadow-lg h-full">
                  <img 
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesa%20grande.JPG"
                    alt="Mesa comunitaria con mosaicos coloridos en ADASTRA"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Pata de elefante */}
                <div className="relative overflow-hidden rounded-2xl shadow-lg h-full">
                  <img 
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Pata%20de%20elefante.JPG"
                    alt="Vista panorámica del patio de ADASTRA con arquitectura única"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Lobby de noche */}
                <div className="relative overflow-hidden rounded-2xl shadow-lg h-full">
                  <img 
                    src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/lobby%20de%20noche.jpg"
                    alt="Lobby nocturno de ADASTRA con iluminación ambiental"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
              </div>
              
            </div>
            
          </div>
          
        </div>
      </section>

      {/* Partners & Stats Section */}
      <section className="bg-white py-12 md:py-20 relative overflow-hidden">
        <div className="container-custom max-w-7xl mx-auto px-4 md:px-6">
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
            
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              
              {/* Main Stats */}
              <div className="space-y-6">
                <h2 className="font-sans text-5xl md:text-6xl lg:text-7xl font-light text-gray-900 leading-tight">
                  15+ socios.
                </h2>
                
                <h2 className="font-sans text-5xl md:text-6xl lg:text-7xl font-light text-gray-900 leading-tight">
                  25+ proveedores.
                </h2>
                
                <h2 className="font-sans text-5xl md:text-6xl lg:text-7xl font-light text-gray-900 leading-tight">
                  100% calidad.
                </h2>
              </div>
              
              {/* Description Text */}
              <div className="max-w-xl">
                <p className="font-sans text-lg md:text-xl text-gray-600 leading-relaxed">
                  Colaboramos con socios locales y proveedores de confianza para garantizar la mejor experiencia. Cada ingrediente, cada servicio, cada detalle respaldado por alianzas sólidas.
                </p>
              </div>
              
              {/* Bottom Keywords */}
              <div className="flex flex-wrap items-center gap-6 md:gap-8">
                <div className="text-gray-700 font-sans font-medium text-sm md:text-base tracking-wider">CUBANA</div>
                <div className="text-gray-400">•</div>
                <div className="text-gray-700 font-sans font-medium text-sm md:text-base tracking-wider">MODERNA</div>
                <div className="text-gray-400">•</div>
                <div className="text-gray-700 font-sans font-medium text-sm md:text-base tracking-wider">FRESH</div>
                <div className="text-gray-400">•</div>
                <div className="text-gray-700 font-sans font-medium text-sm md:text-base tracking-wider">LOCAL</div>
              </div>
              
            </div>
            
            {/* Right Column - Image */}
            <div className="relative h-full min-h-[500px] lg:min-h-[600px]">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Pulpo%20Mediterraneo.jpg"
                alt="Pulpo Mediterráneo - ADASTRA Restaurant"
                className="absolute inset-0 w-full h-full object-cover rounded-2xl"
              />
            </div>
            
          </div>
          
        </div>
      </section>

      {/* Wine Selection Section */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="container-custom max-w-7xl mx-auto px-4 md:px-6">
          
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-sans text-3xl md:text-6xl font-light text-gray-900 leading-tight mb-4 md:mb-6">
              Selección de <span className="text-green-600 font-normal italic">Vinos</span>
            </h2>
            <p className="font-sans text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Descubre nuestra exclusiva carta con más de 40 vinos importados de alta calidad, 
              cuidadosamente seleccionados para complementar cada experiencia gastronómica.
            </p>
          </div>

          {/* Wine Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            
            {/* Left Column - Wine Images */}
            <div className="grid grid-cols-2 gap-4">
              {/* Wine Image 1 - Cava */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg h-[300px] md:h-[400px]">
                <img 
                  src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Cava.jpg"
                  alt="Cava de vinos ADASTRA"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              
              {/* Wine Image 2 - Barra elevada */}
              <div className="relative overflow-hidden rounded-2xl shadow-lg h-[300px] md:h-[400px]">
                <img 
                  src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Barra%20elevada.jpg"
                  alt="Barra elevada con selección de vinos"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="text-center lg:text-left space-y-6">
              <div className="inline-flex items-center space-x-2 bg-green-600/10 px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-green-600/20">
                <span className="text-green-600 font-semibold text-xs md:text-base">+40 ETIQUETAS PREMIUM</span>
              </div>
              
              <h3 className="font-sans text-2xl md:text-4xl font-light text-gray-900 leading-tight">
                Una bodega digna de los mejores paladares
              </h3>
              
              <p className="font-sans text-base md:text-lg text-gray-600 leading-relaxed">
                Desde elegantes tintos españoles hasta frescos blancos franceses, nuestra carta de vinos 
                ha sido curada para ofrecer el maridaje perfecto con cada plato de nuestro menú. 
                Cada botella cuenta una historia de tradición y excelencia.
              </p>

              <div className="space-y-3 pt-4">
                <div className="flex items-center space-x-3 justify-center lg:justify-start">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-gray-700 font-sans text-sm md:text-base">Vinos españoles, franceses e italianos</span>
                </div>
                <div className="flex items-center space-x-3 justify-center lg:justify-start">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-gray-700 font-sans text-sm md:text-base">Temperatura y conservación óptima</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-6">
                <Link 
                  to="/reservas"
                  className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-full max-w-[300px] lg:w-auto"
                >
                  <span>RESERVAR</span>
                </Link>
              </div>
            </div>

          </div>
          
        </div>
      </section>

      {/* Menu Highlights Section - Nuestras Especialidades */}
      <section className="py-12 md:py-20 bg-white relative overflow-hidden">
        <div className="container-custom max-w-7xl mx-auto px-4 md:px-6">
          
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-20">
            <h2 className="font-sans text-3xl md:text-6xl font-light text-gray-900 leading-tight mb-4 md:mb-6">
              Nuestras <span className="text-green-600 font-normal italic">Especialidades</span>
            </h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-green-600 to-transparent mx-auto mb-6 md:mb-8"></div>
            <p className="font-sans text-base md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              Los platos estrella que definen la excelencia culinaria de ADASTRA. Preparados con técnicas artesanales y los mejores ingredientes.
            </p>
          </div>

          {/* Especialidades Grid - 3 Platos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            
            {/* Carpaccio de Pulpo */}
            <Link to="/menu" className="block">
              <div className="relative overflow-hidden rounded-3xl group cursor-pointer h-96 border border-gray-200 hover:border-green-600 transition-colors duration-300 shadow-lg hover:shadow-2xl">
                <img 
                  src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Carpaccio de pulpo.JPG" 
                  alt="Carpaccio de Pulpo ADASTRA"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <span className="inline-block bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mb-2">ESPECIALIDAD</span>
                      <h3 className="font-sans text-2xl font-medium text-white mb-2">Carpaccio de Pulpo</h3>
                      <p className="font-sans text-sm text-gray-200 mb-1">Pulpo mediterráneo con sabores auténticos</p>
                      <p className="font-sans text-xs text-gray-400">Entrante elegante</p>
                    </div>
                    <div className="text-right">
                      <span className="font-sans text-3xl font-light text-white">$6200</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Cordero ADASTRA */}
            <Link to="/menu" className="block">
              <div className="relative overflow-hidden rounded-3xl group cursor-pointer h-96 border border-gray-200 hover:border-green-600 transition-colors duration-300 shadow-lg hover:shadow-2xl">
                <img 
                  src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Cordero%20adastra.jpg" 
                  alt="Cordero ADASTRA"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <span className="inline-block bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mb-2">ESPECIALIDAD</span>
                      <h3 className="font-sans text-2xl font-medium text-white mb-2">Cordero ADASTRA</h3>
                      <p className="font-sans text-sm text-gray-200 mb-1">Cordero premium con hierbas y especias cubanas</p>
                      <p className="font-sans text-xs text-gray-400">Plato estrella</p>
                    </div>
                    <div className="text-right">
                      <span className="font-sans text-3xl font-light text-white">$6500</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Pasta Bellahabana */}
            <Link to="/menu" className="block">
              <div className="relative overflow-hidden rounded-3xl group cursor-pointer h-96 border border-gray-200 hover:border-green-600 transition-colors duration-300 shadow-lg hover:shadow-2xl">
                <img 
                  src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Pasta%20bellaHabana.png" 
                  alt="Pasta Bellahabana ADASTRA"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <span className="inline-block bg-green-600 text-white text-xs font-bold px-2 py-1 rounded mb-2">ESPECIALIDAD</span>
                      <h3 className="font-sans text-2xl font-medium text-white mb-2">Pasta Bellahabana</h3>
                      <p className="font-sans text-sm text-gray-200 mb-1">Pasta artesanal con salsa cremosa y mariscos frescos</p>
                      <p className="font-sans text-xs text-gray-400">Fusión caribeña</p>
                    </div>
                    <div className="text-right">
                      <span className="font-sans text-3xl font-light text-white">$5800</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

          </div>

          {/* Menu CTA Button */}
          <div className="flex justify-center">
            <Link 
              to="/menu" 
              className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-auto"
            >
              <span>MENÚ</span>
            </Link>
          </div>



        </div>
      </section>

      {/* Testimonials Section - Enhanced Modern Design */}
      <section className="py-14 md:py-24 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute top-10 left-10 w-32 h-32 bg-green-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-green-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-custom max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-20">
            <h2 className="font-sans text-3xl md:text-6xl font-light text-gray-900 leading-tight mb-4 md:mb-6">
              Experiencias que <span className="text-green-600 font-normal italic">inspiran</span>
            </h2>
            <p className="font-sans text-base md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-4">
              Las voces de quienes han vivido la magia culinaria de ADASTRA
            </p>
          </div>

          {/* Two Column Layout: Testimonials + Video */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            
            {/* Left Column - Enhanced Testimonial Card */}
            <div>
              <div className="relative bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden h-full">
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-green-50 via-white to-green-50 opacity-50"></div>
                
                <div className="relative z-10 p-8 md:p-12 h-full flex flex-col">
                  {/* Quote Mark */}
                  <div className="absolute top-6 left-6 text-6xl text-green-200 font-serif leading-none">"</div>
                  
                  {/* Testimonial Content */}
                  <div className="pt-8 pb-6 flex-1">
                    <blockquote className="font-sans text-xl md:text-2xl text-gray-800 leading-relaxed font-light italic mb-6 pl-6">
                      {testimonials[currentTestimonial]?.comment}
                    </blockquote>
                    
                    {/* Customer Info */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <img 
                            src={testimonials[currentTestimonial]?.image} 
                            alt="Cliente"
                            className="w-14 h-14 rounded-full object-cover border-4 border-white shadow-lg"
                          />
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                            </svg>
                          </div>
                        </div>
                        <div>
                          <div className="font-sans text-lg font-semibold text-gray-900">{testimonials[currentTestimonial]?.name}</div>
                          <div className="font-sans text-sm text-gray-500">{testimonials[currentTestimonial]?.role}</div>
                        </div>
                      </div>
                      
                      {/* Rating */}
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 24 24">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                          ))}
                        </div>
                        <span className="font-sans text-base font-semibold text-gray-700">5.0</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    <div className="flex space-x-2">
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentTestimonial(index)}
                          className={`w-3 h-3 rounded-full transition-all duration-300 ${
                            index === currentTestimonial 
                              ? 'bg-green-600 scale-110' 
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                        className="w-10 h-10 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center hover:border-green-600 hover:text-green-600 transition-colors shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                        className="w-10 h-10 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center hover:border-green-600 hover:text-green-600 transition-colors shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Video Space */}
            <div className="flex items-center">
              <div className="relative w-full">
                <div className="relative bg-gray-100 rounded-3xl overflow-hidden shadow-xl h-full min-h-[500px]">
                  {/* Video Container - Balanced Size */}
                  <div className="h-[400px]">
                    <video
                      className="w-full h-full object-cover rounded-2xl"
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls
                      poster="/assets/images/restaurant/hero-1.jpg"
                    >
                      <source src={getVideoUrl("Hero Inaguracion.MP4")} type="video/mp4" />
                      Tu navegador no soporta el elemento de video.
                    </video>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-6 -right-6 w-20 h-20 bg-green-600/10 rounded-full"></div>
                <div className="absolute -bottom-8 -left-8 w-28 h-28 bg-green-400/5 rounded-full"></div>
              </div>
            </div>

          </div>

          {/* Enhanced Stats Grid - Minimalist Design */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <div className="p-8 text-center">
              <div className="flex items-baseline justify-center mb-3">
                <span className="font-sans text-4xl font-extralight text-gray-900">4.9</span>
                <svg className="w-6 h-6 text-green-600 ml-2" fill="currentColor" viewBox="0 0 24 24" style={{marginTop: '2px'}}>
                  <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div className="h-px w-8 bg-gray-300 mx-auto mb-3"></div>
              <div className="font-sans text-sm font-medium text-gray-500 uppercase tracking-wider">Rating Promedio</div>
            </div>
            
            <div className="p-8 text-center">
              <div className="font-sans text-4xl font-extralight text-gray-900 mb-3">3,000<span className="text-green-600">+</span></div>
              <div className="h-px w-8 bg-gray-300 mx-auto mb-3"></div>
              <div className="font-sans text-sm font-medium text-gray-500 uppercase tracking-wider">Clientes Satisfechos</div>
            </div>
            
            <div className="p-8 text-center">
              <div className="font-sans text-4xl font-extralight text-gray-900 mb-3">97<span className="text-green-600">%</span></div>
              <div className="h-px w-8 bg-gray-300 mx-auto mb-3"></div>
              <div className="font-sans text-sm font-medium text-gray-500 uppercase tracking-wider">Recomendaciones</div>
            </div>
            
            <div className="p-8 text-center">
              <div className="font-sans text-4xl font-extralight text-gray-900 mb-3">15<span className="text-green-600">+</span></div>
              <div className="h-px w-8 bg-gray-300 mx-auto mb-3"></div>
              <div className="font-sans text-sm font-medium text-gray-500 uppercase tracking-wider">Años Experiencia</div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex justify-center mt-16">
            <Link 
              to="/reservas" 
              className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-auto"
            >
              <span>RESERVAR MESA</span>
            </Link>
          </div>

        </div>
      </section>

      {/* Full-Width Restaurant Atmosphere Section */}
      <section className="relative min-h-[40vh] flex items-center justify-start overflow-hidden py-20 md:py-28">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesa%20de%20patio%20con%20lcientes%20desde%20salon.JPG" 
            alt="Ambiente exclusivo del restaurante"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Content - Centered */}
        <div className="container-custom max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center">
            <h2 className="font-sans text-3xl md:text-6xl font-light text-white leading-tight mb-4 md:mb-6">
              Un espacio único para <span className="text-green-400 font-normal italic">experiencias inolvidables</span>
            </h2>
            <p className="font-sans text-base md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed mb-8 md:mb-12 px-4">
              Nuestro ambiente combina la elegancia contemporánea con la calidez cubana, 
              creando el escenario perfecto para celebraciones especiales y encuentros memorables.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex justify-center">
              <Link 
                to="/reservas" 
                className="border border-white text-white px-8 py-3 font-bold tracking-wide hover:bg-white hover:text-black transition-all duration-300 inline-flex items-center justify-center w-auto"
              >
                <span>RESERVAR MESA</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Mosaic Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="container-custom max-w-7xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="font-sans text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Nuestro <span className="text-green-600 font-normal italic">Espacio</span>
            </h2>
            <p className="font-sans text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Descubre la atmósfera auténtica de Cuba en cada rincón de nuestro restaurante
            </p>
          </div>

          {/* Mosaic Gallery Grid */}
          <div className="grid grid-cols-4 grid-rows-4 gap-4 h-[800px]">
            
            {/* Large image - Restaurant Interior - Salon de Noche */}
            <div className="col-span-2 row-span-2 relative overflow-hidden rounded-3xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Salon%20de%20Noche.JPG" 
                alt="Interior del restaurante ADASTRA de noche"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-6 left-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <h3 className="font-sans text-2xl font-medium mb-2">Salón Principal</h3>
                <p className="font-sans text-sm text-gray-200">Ambiente elegante de noche</p>
              </div>
            </div>

            {/* Mulata */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mulata.jpg" 
                alt="Ambiente ADASTRA"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Lobby de noche */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/lobby%20de%20noche.jpg" 
                alt="Lobby de noche ADASTRA"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Patio Dining - Medium - Mesas de Patio desde 2do */}
            <div className="col-span-1 row-span-2 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesas%20de%20Patio%20desde%202do.JPG" 
                alt="Vista del patio desde arriba"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <h3 className="font-sans text-lg font-medium mb-1">Patio Interior</h3>
                <p className="font-sans text-xs text-gray-200">Vista desde el mezanine</p>
              </div>
            </div>

            {/* Mesa Grande desde escalera */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesa%20Grande%20desde%20escalera.jpg" 
                alt="Mesa grande vista desde escalera"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Barra 12 del dia - Wide */}
            <div className="col-span-2 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Barra%2012%20del%20dia.JPG" 
                alt="Barra del restaurante durante el día"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <h3 className="font-sans text-lg font-medium mb-1">Barra Principal</h3>
                <p className="font-sans text-xs text-gray-200">Ambiente diurno</p>
              </div>
            </div>

            {/* Bucaro */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Bucaro.JPG" 
                alt="Detalles decorativos ADASTRA"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Mesa de Patio Reserva */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mesa%20de%20Patio%20Reserva.JPG" 
                alt="Mesa de patio reservada"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Polaris Coctel - Mantengo */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Polaris%20Coctel.JPG" 
                alt="Cóctel Polaris"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Lamparas de patio y barra */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Lamparas%20de%20patio%20y%20barra.JPG" 
                alt="Lámparas decorativas del patio"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Coleccion - Mantengo */}
            <div className="col-span-1 row-span-1 relative overflow-hidden rounded-2xl group cursor-pointer">
              <img 
                src="https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Bucaro.JPG" 
                alt="Detalles del restaurante"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

          </div>

          {/* Gallery Button - Centered at bottom */}
          <div className="flex justify-center mt-12">
            <Link 
              to="/galeria"
              className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-auto"
            >
              <span>VER GALERÍA COMPLETA</span>
            </Link>
          </div>

        </div>
      </section>

 
      {/* FAQs Section */}
      <section className="py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://pub-f7715da643654e03b4e14e82a2790fb1.r2.dev/Mango 255.jpg')"
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="container-custom max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Left Side - Stats */}
            <div className="space-y-8">
              <div>
                <h2 className="font-sans text-5xl md:text-6xl font-light text-white leading-tight">
                  1 chef.
                </h2>
              </div>
              
              <div>
                <h2 className="font-sans text-5xl md:text-6xl font-light text-white leading-tight">
                  8+ años.
                </h2>
              </div>
              
              <div className="mb-12">
                <h2 className="font-sans text-5xl md:text-6xl font-light text-white leading-tight">
                  +40 platos.
                </h2>
              </div>
              
              {/* Description Text */}
              <div className="max-w-sm">
                <p className="font-sans text-base text-gray-200 leading-relaxed">
                  Nuestra cocina fusiona tradición cubana con técnicas modernas. Cada plato cuenta una historia, cada ingrediente tiene su propósito en nuestra mesa.
                </p>
              </div>
              
              {/* Bottom Logos Row */}
              <div className="flex flex-wrap items-center gap-8 pt-8">
                <div className="text-gray-300 font-sans font-medium text-sm">CUBANA</div>
                <div className="text-gray-300 font-sans font-medium text-sm">MODERNA</div>
                <div className="text-gray-300 font-sans font-medium text-sm">FRESH</div>
                <div className="text-gray-300 font-sans font-medium text-sm">LOCAL</div>
              </div>
            </div>
            
            {/* Right Side - FAQs */}
            <div className="space-y-4">
              <div className="mb-10">
                <h2 className="font-sans text-4xl md:text-5xl font-light text-white leading-tight mb-3">
                  Preguntas Frecuentes
                </h2>
                <div className="w-16 h-0.5 bg-gradient-to-r from-green-400 to-transparent"></div>
              </div>

              {/* FAQ 1 */}
              <div className="group">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleFAQ(0)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-sans text-lg font-medium text-white group-hover:text-green-400 transition-colors">
                        ¿Necesito hacer reserva?
                      </h3>
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {openFAQ === 0 ? '−' : '+'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 0 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="font-sans text-gray-300 leading-relaxed text-sm">
                        Recomendamos hacer reserva especialmente para cenas y fines de semana. Puedes reservar fácilmente a través de nuestra página web.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ 2 */}
              <div className="group">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleFAQ(1)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-sans text-lg font-medium text-white group-hover:text-green-400 transition-colors">
                        ¿Ofrecen opciones vegetarianas?
                      </h3>
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {openFAQ === 1 ? '−' : '+'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 1 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="font-sans text-gray-300 leading-relaxed text-sm">
                        Sí, tenemos una selección de platos vegetarianos y veganos que fusionan tradición cubana con ingredientes frescos y locales.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ 3 */}
              <div className="group">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleFAQ(2)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-sans text-lg font-medium text-white group-hover:text-green-400 transition-colors">
                        ¿Cuál es el horario de atención?
                      </h3>
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {openFAQ === 2 ? '−' : '+'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 2 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="font-sans text-gray-300 leading-relaxed text-sm">
                        Estamos abiertos todos los días de 12:00 PM a 11:00 PM. Te recomendamos verificar horarios especiales en días festivos.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ 4 */}
              <div className="group">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleFAQ(3)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-sans text-lg font-medium text-white group-hover:text-green-400 transition-colors">
                        ¿Hacen delivery o recogida?
                      </h3>
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {openFAQ === 3 ? '−' : '+'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 3 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="font-sans text-gray-300 leading-relaxed text-sm">
                        Sí, ofrecemos servicio de entrega a domicilio y recogida hasta las 9:00 PM. Puedes realizar tu pedido a través de la página de menú.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ 5 */}
              <div className="group">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleFAQ(4)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-sans text-lg font-medium text-white group-hover:text-green-400 transition-colors">
                        ¿Aceptan grupos grandes?
                      </h3>
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {openFAQ === 4 ? '−' : '+'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === 4 ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 pb-6">
                      <p className="font-sans text-gray-300 leading-relaxed text-sm">
                        Sí, aceptamos grupos grandes y tenemos ofertas especiales. Puede contactarnos para más detalles.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
            
          </div>
        </div>
      </section>

      {/* CTA Reservar Final - Fondo Blanco */}
      <section className="relative py-12 md:py-20 bg-white">
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-sans text-3xl md:text-5xl font-light text-gray-900 mb-4 md:mb-6">
            Reserva tu <span className="font-normal italic text-green-600">Experiencia</span>
          </h2>
          <p className="font-sans text-base md:text-lg text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            Cada momento en ADASTRA es único. No dejes que se escape tu próxima experiencia inolvidable.
          </p>
          <div className="flex justify-center">
            <Link 
              to="/reservas"
              className="border border-gray-900 text-gray-900 px-8 py-3 font-bold tracking-wide hover:bg-gray-900 hover:text-white transition-all duration-300 inline-flex items-center justify-center w-auto"
            >
              <span>RESERVAR MESA</span>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;